import { Calendar } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Input } from '@/shared/ui/input'

function isoDateToDisplay(value: string): string {
  const [year, month, day] = value.split('-')
  return year && month && day ? `${day}/${month}/${year}` : value
}

function displayDateToIso(value: string): string {
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!match) return ''

  const [, day, month, year] = match
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))
  const isValidDate =
    date.getUTCFullYear() === Number(year) &&
    date.getUTCMonth() === Number(month) - 1 &&
    date.getUTCDate() === Number(day)

  return isValidDate ? `${year}-${month}-${day}` : ''
}

function normalizeDateDisplayInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8)
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

interface DateInputProps {
  value: string
  onChange: (value: string) => void
  onBlur: () => void
}

export function DateInput({ value, onChange, onBlur }: DateInputProps) {
  const pickerRef = useRef<HTMLInputElement>(null)
  const [displayValue, setDisplayValue] = useState(() => isoDateToDisplay(value))

  useEffect(() => {
    setDisplayValue(isoDateToDisplay(value))
  }, [value])

  function commitDate(nextDisplayValue: string) {
    const nextIsoValue = displayDateToIso(nextDisplayValue)
    if (nextIsoValue) {
      onChange(nextIsoValue)
    }
  }

  return (
    <div className="relative">
      <Input
        inputMode="numeric"
        placeholder="dd/mm/yyyy"
        value={displayValue}
        onChange={(event) => {
          const nextDisplayValue = normalizeDateDisplayInput(event.target.value)
          setDisplayValue(nextDisplayValue)
          if (nextDisplayValue.length === 10) {
            commitDate(nextDisplayValue)
          } else if (nextDisplayValue.length === 0) {
            onChange('')
          }
        }}
        onBlur={() => {
          if (displayValue.length !== 10 || !displayDateToIso(displayValue)) {
            onChange('')
            setDisplayValue('')
          }
          onBlur()
        }}
        className="pr-10"
      />
      <input
        ref={pickerRef}
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        tabIndex={-1}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
      />
      <button
        type="button"
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-zinc-500 hover:text-zinc-900"
        onClick={() => {
          if (pickerRef.current?.showPicker) {
            pickerRef.current.showPicker()
            return
          }
          pickerRef.current?.click()
        }}
        aria-label="Chọn ngày"
      >
        <Calendar className="h-4 w-4" />
      </button>
    </div>
  )
}
